import React, { useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  beginHistoryInteraction,
  endHistoryInteraction,
} from '../domain/document-lifecycle';
import {
  clampImageHeight,
  initialFixedImageHeightPx,
  isFineResizeStep,
  snapImageHeight,
} from '../domain/image-height';
import {
  resolveImageWidthCommit,
  effectiveImageBoxWidthPx,
  snapImageWidthPx,
} from '../domain/image-width';
import { textWidthGuideLineLeft } from '../domain/block-width';
import { readEmailBuilderMobileCanvasLayoutScale } from '../shell/canvas/emailBuilderMobileCanvasLayoutScale';
import type { ImageBlock } from '../domain/types';
import { WidthGuide } from './WidthGuide';
import { CornerResizeIcon } from './CornerResizeIcon';

export type ImageBoxRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type ResizeAxis = 'width' | 'height' | 'both';

type ImageResizeHandlesProps = {
  block: ImageBlock;
  contentMaxWidth: number;
  imageBox: ImageBoxRect;
  naturalSize: { width: number; height: number } | null;
  onPatch: (patch: Partial<ImageBlock>) => void;
};

type DragState = {
  axis: ResizeAxis;
  startX: number;
  startY: number;
  startWidthPx: number;
  startHeightPx: number;
  layoutScale: number;
};

export function ImageResizeHandles({
  block,
  contentMaxWidth,
  imageBox,
  naturalSize,
  onPatch,
}: ImageResizeHandlesProps) {
  const { t } = useTranslation();
  const dragging = useRef(false);
  const dragState = useRef<DragState | null>(null);
  const [widthGuidePx, setWidthGuidePx] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  function currentWidthPx(): number {
    return effectiveImageBoxWidthPx(block, contentMaxWidth);
  }

  function finishDrag(event: PointerEvent<HTMLDivElement>) {
    if (!dragging.current) {
      return;
    }
    dragging.current = false;
    dragState.current = null;
    setWidthGuidePx(null);
    setIsDragging(false);
    endHistoryInteraction();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    event.currentTarget.dataset.dragging = 'false';
  }

  function renderedHeightPx(): number {
    return initialFixedImageHeightPx(
      block,
      contentMaxWidth,
      imageBox.height,
      naturalSize,
    );
  }

  function startDrag(axis: ResizeAxis, event: PointerEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    dragging.current = true;
    setIsDragging(true);
    dragState.current = {
      axis,
      startX: event.clientX,
      startY: event.clientY,
      startWidthPx: currentWidthPx(),
      startHeightPx: renderedHeightPx(),
      layoutScale: readEmailBuilderMobileCanvasLayoutScale(event.currentTarget),
    };
    if (axis === 'width' || axis === 'both') {
      setWidthGuidePx(currentWidthPx());
    }
    beginHistoryInteraction();
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.dataset.dragging = 'true';
  }

  function aspectRatio(): number | null {
    if (!naturalSize || naturalSize.width <= 0 || naturalSize.height <= 0) {
      return null;
    }
    return naturalSize.width / naturalSize.height;
  }

  function applyResize(event: PointerEvent<HTMLDivElement>) {
    const state = dragState.current;
    if (!dragging.current || !state || contentMaxWidth <= 0) {
      return;
    }

    const fine = isFineResizeStep(event);
    const fromCenter = event.altKey;
    const ratio = aspectRatio();
    const lockAspect =
      state.axis === 'both'
        ? ratio !== null && !event.shiftKey
        : Boolean(event.shiftKey && ratio);

    let widthPx = state.startWidthPx;
    let heightPx = state.startHeightPx;

    const layoutScale = state.layoutScale > 0 ? state.layoutScale : 1;

    if (state.axis === 'width' || state.axis === 'both') {
      const deltaX = ((event.clientX - state.startX) / layoutScale) * (fromCenter ? 2 : 1);
      widthPx = snapImageWidthPx(state.startWidthPx + deltaX, contentMaxWidth, fine);
    }

    if (state.axis === 'height' || state.axis === 'both') {
      const deltaY = ((event.clientY - state.startY) / layoutScale) * (fromCenter ? 2 : 1);
      heightPx = snapImageHeight(state.startHeightPx + deltaY, fine);
    }

    if (lockAspect && ratio) {
      if (state.axis === 'width') {
        heightPx = snapImageHeight(widthPx / ratio, fine);
      } else if (state.axis === 'height') {
        widthPx = snapImageWidthPx(heightPx * ratio, contentMaxWidth, fine);
      } else {
        heightPx = snapImageHeight(widthPx / ratio, fine);
      }
    }

    const patch: Partial<ImageBlock> = {
      widthPx: resolveImageWidthCommit(widthPx, contentMaxWidth),
    };

    if (state.axis === 'width' || state.axis === 'both') {
      setWidthGuidePx(widthPx);
    }

    if (state.axis === 'height' || state.axis === 'both') {
      patch.heightMode = 'fixed';
      patch.heightPx = clampImageHeight(heightPx);
    }

    onPatch(patch);
  }

  const showHeightHandles = block.heightMode === 'fixed';
  const showWidthGuide = widthGuidePx != null;
  const activeWidthPx = widthGuidePx ?? currentWidthPx();
  const right = imageBox.left + imageBox.width;
  const bottom = imageBox.top + imageBox.height;
  const midX = imageBox.left + imageBox.width / 2;
  const midY = imageBox.top + imageBox.height / 2;
  const widthLineLeft = textWidthGuideLineLeft(
    block.align,
    block.padding,
    activeWidthPx,
    contentMaxWidth,
  );
  const readoutWidth = `${activeWidthPx}px`;
  const readoutHeight = `${renderedHeightPx()}px`;

  return (
    <div
      className={"email-builder-image-resize-overlay"}
      data-editor-chrome
      data-image-handles="true"
      data-dragging={isDragging ? 'true' : undefined}>
      <div
        className={"email-builder-image-resize-bounds-ring"}
        style={{
          left: imageBox.left,
          top: imageBox.top,
          width: imageBox.width,
          height: imageBox.height,
        }}
        aria-hidden
      />
      {showWidthGuide ? (
        <WidthGuide lineLeft={widthLineLeft} dragging={widthGuidePx != null} />
      ) : null}
      <div
        className={"email-builder-image-resize-handle"}
        data-axis="width"
        data-at-edge="true"
        style={{ left: right, top: midY }}
        tabIndex={-1}
        role="separator"
        aria-orientation="vertical"
        aria-label={t('Resize image width')}
        onPointerDown={event => startDrag('width', event)}
        onPointerMove={applyResize}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        onMouseDown={event => event.stopPropagation()}
        onClick={event => event.stopPropagation()}>
        <span className={"email-builder-image-resize-dot"} />
        <span className={"email-builder-image-resize-readout"}>{readoutWidth}</span>
      </div>
      {showHeightHandles ? (
        <>
          <div
            className={"email-builder-image-resize-handle"}
            data-axis="height"
            data-at-edge="true"
            style={{ left: midX, top: bottom }}
            tabIndex={-1}
            role="separator"
            aria-orientation="horizontal"
            aria-label={t('Resize image height')}
            onPointerDown={event => startDrag('height', event)}
            onPointerMove={applyResize}
            onPointerUp={finishDrag}
            onPointerCancel={finishDrag}
            onMouseDown={event => event.stopPropagation()}
            onClick={event => event.stopPropagation()}>
            <span className={"email-builder-image-resize-dot"} />
            <span className={"email-builder-image-resize-readout"}>{readoutHeight}</span>
          </div>
          <div
            className={"email-builder-image-resize-handle"}
            data-axis="both"
            data-at-edge="true"
            style={{ left: right, top: bottom }}
            tabIndex={-1}
            role="separator"
            aria-label={t(
              'Resize image width and height together. Hold Shift to resize independently.',
            )}
            onPointerDown={event => startDrag('both', event)}
            onPointerMove={applyResize}
            onPointerUp={finishDrag}
            onPointerCancel={finishDrag}
            onMouseDown={event => event.stopPropagation()}
            onClick={event => event.stopPropagation()}>
            <CornerResizeIcon />
            <span className={"email-builder-image-resize-readout"}>
              {readoutWidth} × {readoutHeight}
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
}
